<template>
    <div class="booking-table-picker">
        <div class="booking-table-heading">
            <div>
                <h4>{{ $t("reservationForm.tables.title") }}</h4>
                <p>{{ $t("reservationForm.tables.hint") }}</p>
            </div>
            <span class="booking-table-count">{{ selectedCapacity }} / {{ guests }} {{ $t("reservationForm.tables.seats") }}</span>
        </div>
        <div v-if="selected.length" class="booking-table-selection" aria-live="polite">
            <span class="booking-table-selection-label">{{ $t("reservationForm.tables.selected") }}</span>
            <ul>
                <li v-for="entity in selected" :key="tableSelectionKey(entity)">
                    <span
                        ><strong>{{ label(entity) }}</strong
                        ><small>{{ entity.roomName }}</small></span
                    >
                    <button
                        type="button"
                        :disabled="disabled"
                        :aria-label="$t('reservationForm.tables.remove', { table: label(entity), room: entity.roomName })"
                        @click="toggle(entity)">
                        <feather type="x" aria-hidden="true" />
                    </button>
                </li>
            </ul>
        </div>
        <div v-if="rooms.length" class="booking-table-filters">
            <div>
                <label for="booking-table-search">{{ $t("reservationForm.tables.search") }}</label>
                <input id="booking-table-search" v-model="query" type="search" class="form-control" :disabled="disabled" autocomplete="off" />
            </div>
            <div v-if="rooms.length > 1">
                <label for="booking-table-room">{{ $t("reservationForm.tables.room") }}</label>
                <select id="booking-table-room" v-model="roomId" class="form-select" :disabled="disabled">
                    <option :value="null">{{ $t("reservationForm.tables.allRooms") }}</option>
                    <option v-for="room in rooms" :key="room.id" :value="room.id">{{ room.roomName }}</option>
                </select>
            </div>
        </div>
        <p v-if="!filteredRooms.length" class="booking-empty" role="status">
            {{ $t(rooms.length ? "reservationForm.tables.noResults" : "reservationForm.tables.empty") }}
        </p>
        <fieldset v-for="room in filteredRooms" :key="room.id" class="booking-table-room" :disabled="disabled">
            <legend>{{ room.roomName }}</legend>
            <div class="booking-table-grid">
                <label
                    v-for="entity in room.entities"
                    :key="entity.selectionKey"
                    class="booking-table-choice"
                    :class="{ 'is-selected': isSelected(entity), 'is-unavailable': entity.$isDisabled && !isSelected(entity) }">
                    <input
                        type="checkbox"
                        :checked="isSelected(entity)"
                        :disabled="entity.$isDisabled && !isSelected(entity)"
                        :aria-label="`${label(entity)} · ${room.roomName}`"
                        :aria-describedby="`booking-table-details-${entity.selectionKey}`"
                        @change="toggle(entity)" />
                    <span class="booking-table-name">{{ label(entity) }}</span>
                    <span :id="`booking-table-details-${entity.selectionKey}`" class="booking-table-description">
                        <span>{{ tableCapacity(entity) }} {{ $t("reservationForm.tables.seats") }}</span>
                        <span v-if="entity.type === 'group'" class="booking-table-members">{{ memberNames(entity) }}</span>
                        <span v-if="entity.disabledReason && !isSelected(entity)" class="booking-table-reason">
                            {{ $t(`reservationForm.tables.${entity.disabledReason}`) }}
                        </span>
                        <span v-else-if="isSelected(entity)" class="booking-table-picked">{{ $t("reservationForm.tables.chosen") }}</span>
                    </span>
                </label>
            </div>
        </fieldset>
    </div>
</template>

<script>
import { filterTableRooms, selectedTableCapacity, tableCapacity, tableName, tableSelectionKey } from "./tableSelection.mjs";

export default {
    props: {
        rooms: { type: Array, default: () => [] },
        selected: { type: Array, default: () => [] },
        guests: { type: Number, required: true },
        disabled: Boolean,
    },
    emits: ["change"],
    data: () => ({ query: "", roomId: null }),
    computed: {
        filteredRooms() {
            return filterTableRooms(this.rooms, this.query, this.roomId);
        },
        selectedCapacity() {
            return selectedTableCapacity(this.selected);
        },
    },
    methods: {
        tableCapacity,
        tableSelectionKey,
        label(entity) {
            return this.$t(`reservationForm.tables.${entity.type === "group" ? "group" : "table"}`) + " " + tableName(entity);
        },
        memberNames(entity) {
            return (entity.tables ?? []).map((table) => this.label({ ...table, type: "table" })).join(" + ");
        },
        isSelected(entity) {
            return this.selected.some((item) => tableSelectionKey(item) === tableSelectionKey(entity));
        },
        toggle(entity) {
            if (this.disabled || (entity.$isDisabled && !this.isSelected(entity))) return;
            this.$emit(
                "change",
                this.isSelected(entity)
                    ? this.selected.filter((item) => tableSelectionKey(item) !== tableSelectionKey(entity))
                    : [...this.selected, entity]
            );
        },
    },
};
</script>

<style scoped>
.booking-table-picker {
    min-width: 0;
}
.booking-table-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin: 14px 0;
}
.booking-table-heading h4 {
    margin: 0;
    font-size: 14px;
    color: var(--booking-primary, #083e60);
}
.booking-table-heading p {
    margin: 6px 0 0;
    color: #62798a;
    font-size: 12px;
}
.booking-table-count {
    flex-shrink: 0;
    padding: 6px 9px;
    border-radius: 7px;
    color: var(--booking-primary, #083e60);
    background: var(--booking-selected-bg, #edf4f8);
    font-size: 12px;
}
.booking-table-selection {
    padding: 14px;
    border: 1px solid var(--booking-selected-border, #083e60);
    border-radius: 10px;
    background: var(--booking-selected-bg, #edf4f8);
    margin: 14px 0;
}
.booking-table-selection-label {
    color: var(--booking-selected, #083e60);
    font-size: 11px;
    font-weight: 600;
}
.booking-table-selection ul {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
}
.booking-table-selection li {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fff;
    padding: 8px 10px;
    border-radius: 7px;
    min-width: 0;
    max-width: 100%;
}
.booking-table-selection li > span {
    min-width: 0;
    overflow-wrap: anywhere;
}
.booking-table-selection strong,
.booking-table-selection small {
    display: block;
}
.booking-table-selection strong {
    font-size: 12px;
    color: var(--booking-primary, #083e60);
}
.booking-table-selection small {
    color: #62798a;
    font-size: 11px;
}
.booking-table-selection button {
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 5px;
    background: #f3f6f8;
    color: #405970;
    padding: 6px;
}
.booking-table-selection button svg {
    width: 16px;
    height: 16px;
}
.booking-table-filters {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    gap: 12px;
    margin: 18px 0;
}
.booking-table-filters label {
    display: block;
    font-size: 12px;
}
.booking-table-room {
    min-width: 0;
    padding: 0;
    border: 0;
    margin: 0 0 20px;
}
.booking-table-room legend {
    float: none;
    width: auto;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 650;
    color: #29465c;
}
.booking-table-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(155px, 1fr));
    gap: 10px;
}
.booking-table-grid .booking-table-choice {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18px;
    align-content: start;
    gap: 7px;
    margin: 0;
    padding: 14px;
    border: 1px solid #c8d5df;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    color: #29465c;
}
.booking-table-choice input {
    grid-column: 2;
    grid-row: 1;
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: var(--booking-primary, #083e60);
}
.booking-table-name {
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 650;
    overflow-wrap: anywhere;
}
.booking-table-description {
    display: grid;
    gap: 5px;
    grid-column: 1 / -1;
    font-size: 12px;
    color: #62798a;
    font-weight: 400;
}
.booking-table-members {
    overflow-wrap: anywhere;
}
.booking-table-grid .booking-table-choice:hover:not(.is-unavailable) {
    border-color: var(--booking-selected-border, #083e60);
}
.booking-table-grid .is-selected {
    background: var(--booking-selected-bg, #edf4f8);
    border-color: var(--booking-selected-border, #083e60);
    box-shadow: inset 0 0 0 1px var(--booking-selected-border, #083e60);
}
.booking-table-grid .is-unavailable {
    background: #f6f8fa;
    cursor: not-allowed;
    border-color: #dfe5eb;
}
.booking-table-choice:focus-within {
    box-shadow: 0 0 0 3px var(--booking-focus-shadow, rgba(8, 62, 96, 0.15));
}
.booking-table-reason {
    color: #716453;
    font-size: 11px;
}
.booking-table-picked {
    color: var(--booking-primary, #083e60);
    font-size: 11px;
    font-weight: 600;
}
@media (max-width: 540px) {
    .booking-table-filters {
        grid-template-columns: minmax(0, 1fr);
    }
    .booking-table-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .booking-table-grid .booking-table-choice {
        padding: 12px;
    }
    .booking-table-heading {
        flex-wrap: wrap;
    }
}
</style>
